import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import delay from 'lodash/delay';
import { useSelector } from 'react-redux';
import { FilterSettings } from '@yme/assets/icons';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { Button, CONTAINED_VARIANT, NO_ICON, SIZE_44 } from '@yme/atoms/button';
import { Col, Row } from '@yme/atoms/grid';
import { FilterType } from '@yme/common/constants';
import { useWindowSize } from '@yme/common/hooks';
import { DatePickerWrapper } from '@yme/components/Destinations/ExperienceGrid/components/ExperienceFilters/DatePickerWrapper';
import { getExperienceListingAnalyticsData, getFilterAnalyticsData, } from '@yme/components/Destinations/ExperienceGrid/helpers';
import { DatePicker } from '@yme/molecules/date-picker';
import { DEFAULT_DROPDOWN_RANGE_TO } from '@yme/molecules/date-picker/constants';
import { DatePickerMode } from '@yme/molecules/date-picker/types';
import { CheckboxesDropdown, FilterDropdown, RadioButtonsDropdown, } from '@yme/molecules/filter-dropdown';
import { getRTL } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { RegularTextLevel2, RegularTextLevel3 } from '@yme/tokens/typography';
import { buildKey, DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { FilterContext } from './FiltersContext';
import { countCheckedOptions } from './helpers';
import { FiltersButtonStyled, FiltersListStyled, LabelStyled } from './styled';
export function ExperienceFilters({ cardsPerPage, isFilterCleared }) {
    const [isFiltersBottomSheetOpened, setIsFiltersBottomSheetOpened] = useState(false);
    const isRightToLeft = useSelector(getRTL);
    const { config, dateRange, filteredData, countTotalAppliedValues, appliedFilters, onSelectFilter, onSelectDateRange, onClearFilters, } = useContext(FilterContext);
    const { isInfiniteDesktopView } = useWindowSize();
    const { t } = useTranslation();
    const filtersButtonLabel = countTotalAppliedValues
        ? `${t('filters_filters_label')} (${countTotalAppliedValues})`
        : t('filters_filters_label');
    const footerButtonLabel = `${t('common_showResults_button')} (${filteredData.length})`;
    const datePickerPlaceholder = `${t('destinations_experienceFilterFrom_label')} - 
  ${t('destinations_experienceFilterTo_label')}`;
    const handleOpenMobileFilters = useCallback(() => {
        setIsFiltersBottomSheetOpened(true);
    }, []);
    const handleCloseMobileFilters = useCallback(() => {
        delay(() => setIsFiltersBottomSheetOpened(false), 100);
    }, []);
    const handleClearFilters = useCallback(() => {
        onClearFilters();
        delay(() => setShouldUpdateAnalyticsData(true), 300);
    }, [onClearFilters]);
    const [shouldUpdateAnalyticsData, setShouldUpdateAnalyticsData] = useState(false);
    useEffect(() => {
        if (shouldUpdateAnalyticsData || isFilterCleared) {
            const filterAnalyticsData = getFilterAnalyticsData(appliedFilters, config);
            const analyticsData = getExperienceListingAnalyticsData(filteredData);
            setDigitalData(DigitalDataMutation.ELP_FILTER, {
                category: 'experiences-listing',
                resultCount: filteredData.length,
                pagesCount: Math.ceil(filteredData.length / cardsPerPage),
                listItems: analyticsData,
                filterBy: filterAnalyticsData,
            });
            setShouldUpdateAnalyticsData(false);
        }
    }, [
        shouldUpdateAnalyticsData,
        appliedFilters,
        filteredData,
        cardsPerPage,
        isFilterCleared,
        config,
    ]);
    const Filters = useMemo(() => {
        return (React.createElement(React.Fragment, null,
            config.map((filter, index) => (React.createElement(Col, { lg: 4, key: buildKey('filter', index), as: !isInfiniteDesktopView ? Fragment : '' },
                React.createElement(FilterDropdown, { options: filter.options, titleLabel: filter.title, emptyPlaceholderLabel: t('filters_selectOptions_placeholder'), selectedPlaceholderLabel: t('filters_optionsSelected_placeholder'), totalCountLabels: {
                        all: t('filters_all_label'),
                        of: t('filters_of_label'),
                    }, isRightToLeft: isRightToLeft, type: filter.type, isListItemView: !isInfiniteDesktopView, withTotalCountPlaceholder: true, countAppliedValues: countCheckedOptions(filter.options) }, filter.type === FilterType.RADIO ? (React.createElement(RadioButtonsDropdown, { isRightToLeft: isRightToLeft, name: filter.id, clearAllLabel: t('filters_clearAll_label'), confirmLabel: t('common_confirm_button'), onChange: (option) => {
                        onSelectFilter(filter.id, option);
                        delay(() => setShouldUpdateAnalyticsData(true), 300);
                    } })) : (React.createElement(CheckboxesDropdown, { id: filter.id, labels: {
                        filters_selectAll_label: t('filters_selectAll_label'),
                        filters_clearAll_label: t('filters_clearAll_label'),
                        common_confirm_button: t('common_confirm_button'),
                    }, isRightToLeft: isRightToLeft, onSelect: (option) => {
                        onSelectFilter(filter.id, option);
                        delay(() => setShouldUpdateAnalyticsData(true), 300);
                    }, hasAdditionalControl: Boolean(countTotalAppliedValues) })))))),
            React.createElement(DatePickerWrapper, null,
                React.createElement(LabelStyled, null, t('destinations_experiencesGridFilterDate_label')),
                React.createElement(DatePicker, { date: dateRange, mode: DatePickerMode.RANGE, dropdownRangeFrom: DEFAULT_DROPDOWN_RANGE_TO, dropdownRangeTo: DEFAULT_DROPDOWN_RANGE_TO + 1, isRightToLeft: isRightToLeft, onDateChange: (date) => {
                        onSelectDateRange(date);
                        delay(() => setShouldUpdateAnalyticsData(true), 300);
                    }, bottomSheetLabel: t('destinations_experiencesGridFilterDate_label'), inputPlaceholder: datePickerPlaceholder, isBottomSheetNeeded: true, withFullMonthName: true }))));
    }, [
        config,
        countTotalAppliedValues,
        datePickerPlaceholder,
        dateRange,
        isInfiniteDesktopView,
        isRightToLeft,
        onSelectDateRange,
        onSelectFilter,
        t,
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Row, { mb: "32px", lgMb: "48px", rowGap: "24px" }, isInfiniteDesktopView ? (Filters) : (React.createElement(Col, null,
            React.createElement(FiltersButtonStyled, { isFirstIcon: true, iconType: FilterSettings, onClick: handleOpenMobileFilters }, filtersButtonLabel)))),
        React.createElement(BottomSheet, { initialFocusRef: false, open: isFiltersBottomSheetOpened, header: React.createElement(Header, { headerLeft: React.createElement(RegularTextLevel2, { weight: 500 }, filtersButtonLabel), headerRight: Boolean(countTotalAppliedValues) && (React.createElement(RegularTextLevel3, { as: "span", onClick: handleClearFilters }, t('filters_clearFilters_label'))) }), footer: React.createElement(Button, { size: SIZE_44, hasIconButton: NO_ICON, buttonVariant: CONTAINED_VARIANT, label: footerButtonLabel, onClick: handleCloseMobileFilters }), onDismiss: handleCloseMobileFilters },
            React.createElement(FiltersListStyled, null, Filters))));
}
